import { CdkDrag, CdkDragDrop, CdkDragEnter, CdkDragExit, CdkDropList, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
  OnChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  signal,
  computed,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoPipe } from '@jsverse/transloco';
import { debounceTime, take } from 'rxjs/operators';
import { Asset, CurrentSessionResourcesStore, GuidelineDialogType, Resources, UtcToLocalPipe } from 'shared/util-common';

import { DragAndDropInfoDto } from '@treetop/shared/model';

import { AddAttachmentComponent } from '../add-attachment/add-attachment.component';
import { AddLiteratureComponent } from '../add-literature/add-literature.component';
import { ButtonDirective } from '../button/button.directive';
import { ConfirmationDialogComponent, ConfirmationDialogArgs } from '../confirmation-dialog/confirmation-dialog.component';
import { DialogService } from '../dialog/dialog.service';
import { IconDirective } from '../icon/icon.directive';
import { InputDirective } from '../input';
import { SpinnerComponent } from '../spinner/spinner.component';
import { TooltipDirective } from '../tooltip/tooltip.directive';

@Component({
  selector: 'tt-drag-and-drop',
  templateUrl: './drag-and-drop.component.html',
  standalone: true,
  styleUrls: ['./drag-and-drop.component.scss'],
  imports: [
    MatDialogModule,
    TranslocoPipe,
    CdkDropList,
    CdkDrag,
    UtcToLocalPipe,

    FormsModule,
    AddAttachmentComponent,
    AddLiteratureComponent,
    ReactiveFormsModule,
    MatIconModule,
    IconDirective,
    TooltipDirective,
    InputDirective,
    SpinnerComponent,
    ButtonDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DragAndDropComponent implements OnInit, OnChanges {
  private currentSessionResources = inject(CurrentSessionResourcesStore);
  private dialogService = inject(DialogService);
  private cdr = inject(ChangeDetectorRef);

  private _deletedImportedResourceIds = signal<string[]>([]);
  private _attachedResources = signal<DragAndDropInfoDto[]>([]);
  private _availableResources = signal<DragAndDropInfoDto[]>([]);

  @Input()
  public set deletedImportedResourceIds(ids: string[]) {
    this._deletedImportedResourceIds.set(ids ?? []);
  }

  public get deletedImportedResourceIds(): string[] {
    return this._deletedImportedResourceIds();
  }

  @Input()
  public set attachedResources(resources: DragAndDropInfoDto[]) {
    this._attachedResources.set(resources);
  }

  public get attachedResources(): DragAndDropInfoDto[] {
    return this._attachedResources();
  }

  /**
   * Computes the list of attached resources that are not marked as deletedImported.
   *
   * @returns {DragAndDropInfoDto[]} The filtered list of attached resources.
   */
  protected filteredAttachedResources = computed(() => {
    const deletedIds = this._deletedImportedResourceIds();
    const test = this._attachedResources().filter((resource) => !deletedIds.includes(resource.ID));
    return test;
  });

  @Input() public set availableResources(resources: DragAndDropInfoDto[]) {
    this._availableResources.set(resources ?? []);
  }

  public get availableResources(): DragAndDropInfoDto[] {
    return this._availableResources();
  }

  /**
   * Computes the list of available resources that are not marked as deletedImported.
   *
   * @returns {DragAndDropInfoDto[]} The filtered list of available resources.
   */
  protected filteredAvailableResources = computed(() => {
    const deletedIds = this._deletedImportedResourceIds();
    return this._availableResources().filter((resource) => !deletedIds.includes(resource.ID));
  });

  @Input() public title: string;
  @Input() public dialogType: GuidelineDialogType;
  @Input() public isUploadInProgress = false;
  @Input() public loading = false;
  @Input() public uploading = false;
  @Input() public disableUploadButton = false;
  @Input() public testidDialogType = 'generic';

  @Output() public searchTermChanged = new EventEmitter<string>();
  @Output() public uploadInProgressChanged = new EventEmitter<boolean>();
  @Output() public uploadFile = new EventEmitter<Asset | File>();
  @Output() public fileNameChanged = new EventEmitter<string>();
  @Output() public onUploadBtnClicked = new EventEmitter();
  @Output() public updateResources = new EventEmitter<Resources>();

  @ViewChild('attachedResourceContainer') public attachedResourceContainer: ElementRef;

  public attachment = signal<Asset>(undefined);

  protected searchTerm = new UntypedFormControl();
  protected attachedSearchTerm: string;
  protected searchedAvailableResources: DragAndDropInfoDto[];

  public ngOnInit(): void {
    this.searchTerm.valueChanges.pipe(debounceTime(300)).subscribe((searchTerm: string) => {
      this.searchTermChanged.emit(searchTerm);
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.isUploadInProgress && this.attachedResourceContainer) {
      this.attachedResourceContainer.nativeElement.style.display = this.isUploadInProgress ? 'none' : 'block';
    }
  }

  /**
   * Handles the drop event for drag-and-drop functionality.
   * Moves or transfers items between lists and updates the corresponding signals and UI state.
   *
   * @param {CdkDragDrop<DragAndDropInfoDto[]>} event - The drag-and-drop event containing information about the item being moved and the containers involved.
   *
   * - If the item is moved within the same container, it updates the order of items.
   * - If the item is transferred between containers:
   *   - Opens a confirmation dialog if the item is imported and moved to the available resources list.
   *   - Updates the attached or available resources based on the target container.
   *   - Emits the updated resources to the parent component.
   */
  protected drop(event: CdkDragDrop<DragAndDropInfoDto[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      const item = event.previousContainer.data[event.previousIndex] as DragAndDropInfoDto;
      if (item.imported && event.container.id === 'availableResourceList') {
        const args: ConfirmationDialogArgs = {
          message: 'SingleGuidelineManager.ImportedGuideline.AttachmentDialog.DeleteWarning',
          cancelButtonCaption: 'Cancel',
          confirmButtonCaption: 'Delete',
        };
        this.dialogService
          .open(ConfirmationDialogComponent, {
            icon: 'triangle-alert',
            title: 'SingleGuidelineManager.ImportedGuideline.AttachmentDialog.DeleteTitle',
            data: args,
          })
          .afterClosed()
          .pipe(take(1))
          .subscribe((result) => {
            if (result) {
              const prevArray = event.previousContainer.data as DragAndDropInfoDto[];
              const currArray = event.container.data as DragAndDropInfoDto[];
              const idx = prevArray.findIndex((el) => el.ID === item.ID);

              if (idx > -1) {
                const [removed] = prevArray.splice(idx, 1);
                currArray.splice(event.currentIndex, 0, removed);
                this.attachedSearchTerm = '';
                this.processRemoveImportedResource(item);
                this.cdr.detectChanges();
              }
            }
          });
      } else {
        transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);
        this.attachedSearchTerm = '';
        if (event.container.id === 'attachedResourceList') {
          this.addAttachedResource(event.item.data);
        } else if (event.container.id === 'availableResourceList') {
          this.removeAttachedResource(event.item.data);
        }
      }
    }
  }

  protected entered(event: CdkDragEnter) {
    //Todo Implement nice highlighting
  }

  protected onDragStart() {
    this.setDropZonesActive(true);
  }

  protected onDragEnd() {
    this.setDropZonesActive(false);
  }

  protected exited(event: CdkDragExit) {
    //Todo Implement nice highlighting
  }

  protected onUploadStarted() {
    this.isUploadInProgress = true;
    if (this.attachedResourceContainer) {
      this.attachedResourceContainer.nativeElement.style.display = 'none';
    }
    this.uploadInProgressChanged.emit(true);
  }

  protected onUploadCancel() {
    this.isUploadInProgress = false;
    if (this.attachedResourceContainer) {
      this.attachedResourceContainer.nativeElement.style.display = 'block';
    }
    this.uploadInProgressChanged.emit(false);
  }

  protected addAttachment(data: Asset) {
    this.uploadFile.emit(data);
  }

  protected handleAssetNameChanged(name: string) {
    this.fileNameChanged.emit(name);
  }

  protected addLiterature(file: File) {
    this.uploadFile.emit(file);
  }

  protected onUploadClicked() {
    this.onUploadBtnClicked.emit();
  }

  /**
   * Removes an imported resource after user confirmation.
   * Opens a confirmation dialog to ensure the user wants to delete the resource.
   * If confirmed, processes the removal of the resource and updates the state.
   *
   * @param {DragAndDropInfoDto} item - The resource to be removed.
   */
  protected removeImportedResource(item: DragAndDropInfoDto) {
    const args: ConfirmationDialogArgs = {
      message: 'SingleGuidelineManager.ImportedGuideline.AttachmentDialog.DeleteWarning',
      cancelButtonCaption: 'Cancel',
      confirmButtonCaption: 'Delete',
    };
    this.dialogService
      .open(ConfirmationDialogComponent, {
        icon: 'triangle-alert',
        title: 'SingleGuidelineManager.ImportedGuideline.AttachmentDialog.DeleteTitle',
        data: args,
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((result) => {
        if (result) {
          this.processRemoveImportedResource(item);
        }
      });
  }

  protected removeAttachedResource(item: DragAndDropInfoDto) {
    this._attachedResources.set(this._attachedResources().filter((el) => el.ID !== item.ID));
    this.updateResources.emit({
      attachedResources: this.filteredAttachedResources(),
      availableResources: this.availableResources,
      deletedImportedResourceIds: this.deletedImportedResourceIds,
    });
  }

  protected addAttachedResource(item: DragAndDropInfoDto) {
    this.availableResources = this.availableResources.filter((el) => el.ID !== item.ID);
    this._attachedResources.set([item, ...this._attachedResources()]);
    this.updateResources.emit({ attachedResources: this.filteredAttachedResources(), availableResources: this.availableResources });
  }

  protected onSearchAttached(event) {
    const searchTerm = event.target.value as string;

    this.searchedAvailableResources = this._attachedResources().filter((el) => {
      return el.Strong.toLowerCase().includes(searchTerm);
    });
  }

  protected setDropZonesActive(isActive: boolean) {
    // logic to show hide drop zone
    const dropZones = document.getElementById('overlay');

    if (isActive) {
      dropZones.classList.remove('hidden');
    } else {
      dropZones.classList.add('hidden');
    }
  }

  /**
   * Processes the removal of an imported resource.
   * Updates the attached resources and deleted resource IDs signals, ensuring the state is consistent.
   * Emits the updated resources to the parent component.
   *
   * @param {DragAndDropInfoDto} item - The resource to be removed.
   */
  private processRemoveImportedResource(item: DragAndDropInfoDto) {
    this.attachedResources = this.attachedResources.filter((el) => el.ID !== item.ID);
    const deletedIds = [...this._deletedImportedResourceIds(), item.ID];
    this._deletedImportedResourceIds.set(deletedIds);
    this.updateResources.emit({
      attachedResources: this.filteredAttachedResources(),
      availableResources: this.availableResources,
      deletedImportedResourceIds: this.deletedImportedResourceIds,
    });
  }

  protected dialogTypes = GuidelineDialogType;
}
